import { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Mail, LogOut, ArrowLeft, Users, Send, UserCog, ScrollText,
  PanelLeftClose, PanelLeft, FileSpreadsheet, Building2, Menu, ShieldCheck,
  ClipboardCheck,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useAdminAuth } from '../context/AdminAuthContext';
import { canAccessLegalizacao } from './legalizacaoAccess';

/* ------------------------------------------------------------------ */
/*  Sidebar sections                                                   */
/* ------------------------------------------------------------------ */

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const sidebarSections: NavSection[] = [
  {
    label: 'E-mail',
    items: [
      { name: 'Relatórios', href: '/admin/dashboard/emails', icon: Mail },
      { name: 'Disparador', href: '/admin/dashboard/mailer', icon: Send },
      { name: 'Regionais', href: '/admin/dashboard/regionais', icon: Users },
    ],
  },
  {
    label: 'Dados',
    items: [
      { name: 'Planilha', href: '/admin/dashboard/planilha', icon: FileSpreadsheet },
      { name: 'Tributário', href: '/admin/dashboard/tributario', icon: ShieldCheck },
      { name: 'Overrides', href: '/admin/dashboard/overrides-unidades', icon: Building2 },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { name: 'Contas', href: '/admin/dashboard/contas', icon: UserCog },
      { name: 'Logs', href: '/admin/dashboard/logs', icon: ScrollText },
    ],
  },
];

/** Secoes visiveis apenas para o dominio interno. */
const internalSections: NavSection[] = [
  {
    label: 'Interno',
    items: [
      { name: 'Legalizacao', href: '/admin/dashboard/legalizacao', icon: ClipboardCheck },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  SidebarNavItem                                                     */
/* ------------------------------------------------------------------ */

function SidebarNavItem({
  item,
  collapsed,
  onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  onClick?: () => void;
}) {
  const { pathname } = useLocation();
  const isActive = pathname === item.href;

  const classes = cn(
    'flex items-center rounded-md text-sm font-medium transition-colors',
    'hover:bg-accent hover:text-accent-foreground',
    isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
    collapsed ? 'h-9 w-9 justify-center mx-auto' : 'gap-3 px-3 py-2'
  );

  const link = (
    <NavLink to={item.href} onClick={onClick} className={classes}>
      <item.icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.name}</span>}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={10}>
          {item.name}
        </TooltipContent>
      </Tooltip>
    );
  }

  return link;
}

/* ------------------------------------------------------------------ */
/*  Section label (expanded only) / thin divider (collapsed)           */
/* ------------------------------------------------------------------ */

function SectionHeader({ label, collapsed }: { label: string; collapsed: boolean }) {
  if (collapsed) {
    return <div className="mx-2.5 my-2 h-px bg-border" />;
  }
  return (
    <p className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
      {label}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/*  Mobile navigation                                                  */
/* ------------------------------------------------------------------ */

function MobileNav({ sections, onNavigate }: { sections: NavSection[]; onNavigate: () => void }) {
  const navigate = useNavigate();

  return (
    <nav className="flex flex-col py-2">
      {sections.map((section, idx) => (
        <div key={section.label}>
          {idx > 0 && <div className="mx-2 my-1 h-px bg-border" />}
          <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            {section.label}
          </p>
          <div className="space-y-0.5">
            {section.items.map((item) => (
              <SidebarNavItem key={item.name} item={item} collapsed={false} onClick={onNavigate} />
            ))}
          </div>
        </div>
      ))}

      <div className="mx-2 my-2 h-px bg-border" />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => { onNavigate(); navigate('/'); }}
        className="justify-start gap-3 mx-1 text-muted-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar ao Dashboard
      </Button>
    </nav>
  );
}

/* ------------------------------------------------------------------ */
/*  AdminLayout                                                        */
/* ------------------------------------------------------------------ */

export function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isFullAdmin = user?.accessLevel === 'admin';
  const visibleSections = isFullAdmin
    ? (canAccessLegalizacao(user?.email) ? [...sidebarSections, ...internalSections] : sidebarSections)
    : (canAccessLegalizacao(user?.email) ? internalSections : []);

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        {/* ── Header ─────────────────────────────────────────────── */}
        <header className="flex h-14 items-center justify-between border-b bg-card px-4 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 md:hidden">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-4">
                <SheetHeader className="text-left mb-1">
                  <SheetTitle className="text-base">Menu Admin</SheetTitle>
                </SheetHeader>
                <MobileNav sections={visibleSections} onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>

            {/* Desktop sidebar toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed(!collapsed)}
              className="h-8 w-8 hidden md:inline-flex"
            >
              {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </Button>

            <h1 className="text-base font-semibold sm:text-lg">Área do Administrador</h1>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-xs sm:text-sm text-muted-foreground hidden sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-2">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Sair</span>
            </Button>
          </div>
        </header>

        {/* ── Body ───────────────────────────────────────────────── */}
        <div className="flex h-[calc(100vh-3.5rem)]">
          {/* Desktop Sidebar */}
          <aside
            className={cn(
              'border-r bg-card h-full transition-all duration-200 flex flex-col shrink-0 hidden md:flex',
              collapsed ? 'w-[52px]' : 'w-56 lg:w-60'
            )}
          >
            {/* Nav sections */}
            <nav className="flex-1 overflow-y-auto px-1.5 py-1">
              {visibleSections.map((section) => (
                <div key={section.label}>
                  <SectionHeader label={section.label} collapsed={collapsed} />
                  <div className="space-y-0.5">
                    {section.items.map((item) => (
                      <SidebarNavItem key={item.name} item={item} collapsed={collapsed} />
                    ))}
                  </div>
                </div>
              ))}
            </nav>

            {/* Footer: voltar */}
            <div className="border-t p-1.5">
              {collapsed ? (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => navigate('/')}
                      className="flex h-9 w-9 items-center justify-center rounded-md mx-auto text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={10}>
                    Voltar ao Dashboard
                  </TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/')}
                  className="w-full justify-start gap-3 text-muted-foreground"
                >
                  <ArrowLeft className="h-4 w-4 shrink-0" />
                  <span className="truncate">Voltar ao Dashboard</span>
                </Button>
              )}
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
